import { priceUnitShort, priceUnitLabel } from "@/lib/catalog-presentation";
import { useEffect, useMemo, useState } from "react";
import { useMoney } from "@/lib/currency";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CalendarDays, Users, Loader2, Minus, Plus, MapPin } from "lucide-react";

import { getPublicAvailability, resolvePricingForDate } from "@/lib/booking-ops.functions";
import { startCustomTourCheckout } from "@/lib/custom-checkout.functions";
import { getStripeEnvironment, isStripeConfigured } from "@/lib/stripe-browser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { normalizeLang } from "@/lib/lang";
import type { UnifiedTourDetail } from "@/lib/catalog.functions";

type Tier = "adult" | "child" | "infant" | "student" | "senior" | "private" | "group";

const TIER_LABELS: Record<Tier, string> = {
  adult: "Adulto",
  child: "Menor",
  infant: "Infante",
  student: "Estudiante",
  senior: "3ra edad",
  private: "Privado",
  group: "Grupal",
};

type Extra = NonNullable<UnifiedTourDetail["bookingExtras"]>[number];
type Question = NonNullable<UnifiedTourDetail["bookingQuestions"]>[number];
type MeetingPoint = NonNullable<UnifiedTourDetail["meetingPoints"]>[number];

export function CustomTourBookingPanel({
  tourId,
  fallbackPriceFrom,
  fallbackCurrency,
  extras = [],
  questions = [],
  meetingPoints = [],
  minParticipants = null,
  maxParticipants = null,
  bookingCutoffHours = null,
  requiresHotelInfo = false,
  requiresFlightInfo = false,
  hasTerms = false,
}: {
  tourId: string;
  fallbackPriceFrom: number | null;
  fallbackCurrency: string;
  extras?: Extra[];
  questions?: Question[];
  meetingPoints?: MeetingPoint[];
  minParticipants?: number | null;
  maxParticipants?: number | null;
  bookingCutoffHours?: number | null;
  requiresHotelInfo?: boolean;
  requiresFlightInfo?: boolean;
  hasTerms?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const in90 = new Date(Date.now() + 90 * 86400_000).toISOString().slice(0, 10);

  const availQ = useQuery({
    queryKey: ["custom-availability", tourId],
    queryFn: () => getPublicAvailability({ data: { tourId, from: today, to: in90 } }),
    staleTime: 60_000,
  });

  const slots = availQ.data?.slots ?? [];
  const cutoffMs = (bookingCutoffHours ?? 0) * 3600_000;
  // Hide slots the server would reject for minimum advance notice.
  const bookableSlots = useMemo(() => {
    if (cutoffMs <= 0) return slots;
    return slots.filter((s) => {
      const startsAt = new Date(`${s.date}T${s.time ?? "00:00:00"}`).getTime();
      return !Number.isFinite(startsAt) || startsAt - Date.now() >= cutoffMs;
    });
  }, [slots, cutoffMs]);

  const dates = useMemo(() => Array.from(new Set(bookableSlots.map((s) => s.date))), [bookableSlots]);
  const [date, setDate] = useState<string>("");
  useEffect(() => {
    if (!date && dates.length) setDate(dates[0]);
  }, [dates, date]);

  const slotsForDate = bookableSlots.filter((s) => s.date === date);
  const [slotId, setSlotId] = useState<string>("");
  useEffect(() => {
    if (slotsForDate.length) setSlotId(slotsForDate[0].id);
    else setSlotId("");
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const priceQ = useQuery({
    queryKey: ["custom-pricing", tourId, date],
    queryFn: () => resolvePricingForDate({ data: { tourId, date } }),
    enabled: !!date,
    staleTime: 60_000,
  });
  const prices = (priceQ.data?.prices ?? {}) as Partial<Record<Tier, number>>;
  const currency = priceQ.data?.currency ?? fallbackCurrency;
  const money = useMoney();
  const availableTiers = (Object.keys(prices) as Tier[]).filter((t) => prices[t] != null);

  const [qty, setQty] = useState<Partial<Record<Tier, number>>>({});
  useEffect(() => {
    setQty(availableTiers.includes("adult") ? { adult: 2 } : {});
  }, [JSON.stringify(availableTiers)]); // eslint-disable-line react-hooks/exhaustive-deps

  const paxSubtotal = availableTiers.reduce((s, t) => s + (prices[t] ?? 0) * (qty[t] ?? 0), 0);
  const totalPax = Object.values(qty).reduce<number>((s, q) => s + (q ?? 0), 0);

  // --- Extras -------------------------------------------------------------
  const [extraQty, setExtraQty] = useState<Record<string, number>>({});
  useEffect(() => {
    const initial: Record<string, number> = {};
    for (const ex of extras) if (ex.required) initial[ex.id] = 1;
    setExtraQty(initial);
  }, [extras.map((e) => e.id).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const extrasTotal = extras.reduce((sum, ex) => {
    const q = extraQty[ex.id] ?? 0;
    if (q <= 0) return sum;
    const units = ex.chargeType === "per_person" ? q * totalPax : q;
    return sum + ex.price * units;
  }, 0);

  // --- Meeting point ------------------------------------------------------
  const [meetingPointId, setMeetingPointId] = useState<string>("");
  useEffect(() => {
    if (!meetingPointId && meetingPoints.length) setMeetingPointId(meetingPoints[0].id);
  }, [meetingPoints, meetingPointId]);
  const selectedPoint = meetingPoints.find((mp) => mp.id === meetingPointId) ?? null;
  const pickupSurcharge = selectedPoint?.extraPrice ?? 0;

  // --- Questions / hotel --------------------------------------------------
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [hotel, setHotel] = useState({ name: "", address: "", room: "", flightNumber: "", arrivalTime: "", notes: "" });
  const [terms, setTerms] = useState(false);

  const subtotal = paxSubtotal + extrasTotal + pickupSurcharge;
  const selectedSlot = slotsForDate.find((s) => s.id === slotId);
  const missingAnswers = questions.some((q) => q.required && !(answers[q.id] ?? "").trim());
  const canBook =
    !!slotId &&
    totalPax > 0 &&
    paxSubtotal > 0 &&
    !!selectedSlot &&
    selectedSlot.available >= totalPax &&
    (!minParticipants || totalPax >= minParticipants) &&
    (!maxParticipants || totalPax <= maxParticipants);

  const canPay =
    canBook &&
    !missingAnswers &&
    (!requiresHotelInfo || !!hotel.name.trim()) &&
    (!requiresFlightInfo || !!hotel.flightNumber.trim()) &&
    (!hasTerms || terms);

  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [openCheckout, setOpenCheckout] = useState(false);

  const startMut = useMutation({
    mutationFn: () =>
      startCustomTourCheckout({
        data: {
          tourId,
          availabilityId: slotId,
          items: availableTiers.map((t) => ({ tier: t, quantity: qty[t] ?? 0 })),
          customer: { ...form, country: "MX", language: normalizeLang(i18n.language) },
          environment: getStripeEnvironment(),
          extras: extras
            .map((ex) => ({ id: ex.id, quantity: extraQty[ex.id] ?? 0 }))
            .filter((e) => e.quantity > 0),
          answers: questions
            .filter((q) => (answers[q.id] ?? "").trim())
            .map((q) => ({ questionId: q.id, value: answers[q.id].trim() })),
          meetingPointId: meetingPointId || null,
          hotelInfo: hotel,
          termsAccepted: terms,
        },
      }),
    onSuccess: (res) => {
      if (res.ok && res.url) window.location.href = res.url;
    },
  });

  if (availQ.isLoading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 lg:sticky lg:top-24">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!bookableSlots.length) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 lg:sticky lg:top-24">
        <p className="text-sm text-muted-foreground">
          Próximamente hay nuevas fechas disponibles. Contáctanos para reservar este tour de manera personalizada.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-elevated)] lg:sticky lg:top-24">
      <div>
        <div className="font-display text-2xl font-bold">
          {money(prices.adult ?? fallbackPriceFrom ?? 0, currency, 0)}{" "}
          <span className="text-sm font-medium text-muted-foreground">{priceUnitLabel({} as never, i18n.language)}</span>
        </div>
        {priceQ.data?.season && (
          <div className="text-[11px] uppercase tracking-wide text-primary">Tarifa {priceQ.data.season.name}</div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="inline-flex items-center gap-1.5 text-xs"><CalendarDays className="h-3.5 w-3.5" /> Fecha</Label>
        <select
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          {dates.map((d) => (<option key={d} value={d}>{d}</option>))}
        </select>
      </div>

      {slotsForDate.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs">Horario</Label>
          <div className="flex flex-wrap gap-2">
            {slotsForDate.map((s) => (
              <button
                key={s.id}
                onClick={() => setSlotId(s.id)}
                className={`rounded-full border px-3 py-1.5 text-xs ${slotId === s.id ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}
                disabled={s.available <= 0}
              >
                {s.time ?? "—"} · {s.available} disp.
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2 border-t pt-3">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Users className="h-3.5 w-3.5" /> Viajeros
        </div>
        {availableTiers.length === 0 && <p className="text-xs text-muted-foreground">No hay tarifas configuradas para esta fecha.</p>}
        {availableTiers.map((t) => (
          <div key={t} className="flex items-center justify-between text-sm">
            <div>
              <div className="font-medium">{TIER_LABELS[t]}</div>
              <div className="text-xs text-muted-foreground">{money(prices[t] ?? 0, currency, 0)}</div>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setQty((q) => ({ ...q, [t]: Math.max(0, (q[t] ?? 0) - 1) }))}
                className="rounded-full border p-1 hover:bg-accent"
                aria-label="Restar"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-6 text-center font-semibold">{qty[t] ?? 0}</span>
              <button
                type="button"
                onClick={() => setQty((q) => ({ ...q, [t]: (q[t] ?? 0) + 1 }))}
                className="rounded-full border p-1 hover:bg-accent"
                aria-label="Sumar"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {!!minParticipants && totalPax > 0 && totalPax < minParticipants && (
          <p className="text-xs text-destructive">Mínimo {minParticipants} viajeros.</p>
        )}
        {!!maxParticipants && totalPax > maxParticipants && (
          <p className="text-xs text-destructive">Máximo {maxParticipants} viajeros por reserva.</p>
        )}
      </div>

      {extras.length > 0 && (
        <div className="space-y-2 border-t pt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Extras</div>
          {extras.map((ex) => (
            <div key={ex.id} className="flex items-start justify-between gap-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium">
                  {ex.title}
                  {ex.required && <span className="ml-1 text-xs text-primary">(incluido obligatorio)</span>}
                </div>
                {ex.description && <div className="text-xs text-muted-foreground">{ex.description}</div>}
                <div className="text-xs text-muted-foreground">
                  {money(ex.price, ex.currency || currency, 0)}
                  {ex.chargeType === "per_person" ? " / persona" : " / reserva"}
                </div>
              </div>
              <div className="inline-flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() =>
                    setExtraQty((q) => ({ ...q, [ex.id]: Math.max(ex.required ? 1 : 0, (q[ex.id] ?? 0) - 1) }))
                  }
                  className="rounded-full border p-1 hover:bg-accent"
                  aria-label={`Restar ${ex.title}`}
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-6 text-center font-semibold">{extraQty[ex.id] ?? 0}</span>
                <button
                  type="button"
                  onClick={() =>
                    setExtraQty((q) => {
                      const next = (q[ex.id] ?? 0) + 1;
                      if (ex.maxQuantity && next > ex.maxQuantity) return q;
                      return { ...q, [ex.id]: next };
                    })
                  }
                  className="rounded-full border p-1 hover:bg-accent"
                  aria-label={`Sumar ${ex.title}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {meetingPoints.length > 0 && (
        <div className="space-y-1.5 border-t pt-3">
          <Label className="inline-flex items-center gap-1.5 text-xs">
            <MapPin className="h-3.5 w-3.5" /> Punto de encuentro o recogida
          </Label>
          <select
            value={meetingPointId}
            onChange={(e) => setMeetingPointId(e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {meetingPoints.map((mp) => (
              <option key={mp.id} value={mp.id}>
                {mp.name}
                {mp.extraPrice > 0 ? ` (+${money(mp.extraPrice, currency, 0)})` : ""}
              </option>
            ))}
          </select>
          {selectedPoint?.address && (
            <p className="text-xs text-muted-foreground">{selectedPoint.address}</p>
          )}
        </div>
      )}

      <div className="space-y-1 border-t pt-3 text-sm">
        {extrasTotal > 0 && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Extras</span>
            <span>{money(extrasTotal, currency)}</span>
          </div>
        )}
        {pickupSurcharge > 0 && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Recogida</span>
            <span>{money(pickupSurcharge, currency)}</span>
          </div>
        )}
        <div className="flex items-center justify-between font-semibold">
          <span>Total</span>
          <span>{money(subtotal, currency)}</span>
        </div>
      </div>

      {!openCheckout ? (
        <Button className="w-full" disabled={!canBook} onClick={() => setOpenCheckout(true)}>
          Reservar
        </Button>
      ) : (
        <div className="space-y-2 border-t pt-3">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Nombre" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            <Input placeholder="Apellido" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </div>
          <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input placeholder="Teléfono (opcional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />

          {(requiresHotelInfo || requiresFlightInfo) && (
            <div className="space-y-2 border-t pt-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Datos de hospedaje y llegada
              </div>
              {requiresHotelInfo && (
                <>
                  <Input placeholder="Hotel" value={hotel.name} onChange={(e) => setHotel({ ...hotel, name: e.target.value })} />
                  <Input placeholder="Dirección del hotel (opcional)" value={hotel.address} onChange={(e) => setHotel({ ...hotel, address: e.target.value })} />
                  <Input placeholder="Habitación (opcional)" value={hotel.room} onChange={(e) => setHotel({ ...hotel, room: e.target.value })} />
                </>
              )}
              {requiresFlightInfo && (
                <>
                  <Input placeholder="Número de vuelo" value={hotel.flightNumber} onChange={(e) => setHotel({ ...hotel, flightNumber: e.target.value })} />
                  <Input placeholder="Hora de llegada (opcional)" value={hotel.arrivalTime} onChange={(e) => setHotel({ ...hotel, arrivalTime: e.target.value })} />
                </>
              )}
            </div>
          )}

          {questions.length > 0 && (
            <div className="space-y-2 border-t pt-2">
              {questions.map((q) => (
                <div key={q.id} className="space-y-1">
                  <Label className="text-xs" htmlFor={`q-${q.id}`}>
                    {q.label}
                    {q.required && <span className="ml-0.5 text-destructive">*</span>}
                  </Label>
                  {q.fieldType === "select" && q.options.length > 0 ? (
                    <select
                      id={`q-${q.id}`}
                      value={answers[q.id] ?? ""}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Selecciona…</option>
                      {q.options.map((op) => (<option key={op} value={op}>{op}</option>))}
                    </select>
                  ) : (
                    <Input
                      id={`q-${q.id}`}
                      type={q.fieldType === "date" ? "date" : q.fieldType === "number" ? "number" : "text"}
                      value={answers[q.id] ?? ""}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                    />
                  )}
                  {q.helpText && <p className="text-[11px] text-muted-foreground">{q.helpText}</p>}
                </div>
              ))}
            </div>
          )}

          {hasTerms && (
            <label className="flex items-start gap-2 border-t pt-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input"
              />
              <span>Acepto los términos y condiciones y la política de cancelación.</span>
            </label>
          )}

          {startMut.data && !startMut.data.ok && (
            <p className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{startMut.data.error}</p>
          )}
          <Button
            className="w-full"
            disabled={!canPay || !form.firstName || !form.lastName || !form.email || !isStripeConfigured() || startMut.isPending}
            onClick={() => startMut.mutate()}
          >
            {startMut.isPending ? "Preparando pago…" : `Pagar ${money(subtotal, currency)}`}
          </Button>
        </div>
      )}
    </div>
  );
}
